import React, { useEffect, useState } from 'react';
import { MapPin, Clock, CalendarDays, User, Trash2, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getDocs, collection, deleteDoc, doc,updateDoc,  arrayUnion, getDoc  } from 'firebase/firestore';
import { db } from '../Firebase';
import { useUser } from '../UserContext';

export default function LocalAccessHubsPage() {
  const { currentUser } = useUser();
  const [allHubs, setAllHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate=useNavigate()
  useEffect(() => {
    let isMounted = true;
    const fetchHubs = async () => {
      setLoading(true);
      setError(null);
      try {
        const hubsRef = collection(db, 'hubs');
        const querySnapshot = await getDocs(hubsRef);
        const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sorted = docs
          .slice()
          .sort((a, b) => {
            const aTs = a.createdAt?.seconds || 0;
            const bTs = b.createdAt?.seconds || 0;
            return bTs - aTs;
          });
        if (isMounted) setAllHubs(sorted);
      } catch (e) {
        console.error('Error fetching hubs:', e);
        if (isMounted) setError('Failed to load hubs. Please try again later.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchHubs();
    return () => { isMounted = false; };
  }, []);

  const handleDelete = async (hubId) => {
    if (!currentUser) return;
    
    if (window.confirm('Are you sure you want to delete this hub? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'hubs', hubId));
        setAllHubs(prevHubs => prevHubs.filter(hub => hub.id !== hubId));
      } catch (error) {
        console.error('Error deleting hub:', error);
        alert('Failed to delete hub. Please try again.');
      }
    }
  };

  const handleJoinHub = async (hubId) => {
    if (!currentUser) return navigate('/login');
  
    try {
      const hubRef = doc(db, "hubs", hubId);
      const hubSnap = await getDoc(hubRef);
  
      if (hubSnap.exists()) {
        const hubData = hubSnap.data();
  
        // Prevent duplicate join
        const alreadyJoined = (hubData.members || []).some(
          (m) => m.uid === currentUser.uid
        );
        if (alreadyJoined) {
          alert("You already joined this hub!");
          return;
        }
  
        // Add new member
        await updateDoc(hubRef, {
          members: arrayUnion({
            uid: currentUser.uid,
            name: currentUser.displayName || "Anonymous",
          }),
          memberCount: (hubData.memberCount || 0) + 1,
        });
  
        alert("Successfully joined hub!");
      }
    } catch (error) {
      console.error("Error joining hub:", error);
      alert("Failed to join hub. Please try again.");
    }
  };
  return (
    <div className="container mx-auto px-4 py-12 ">
      <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">Local Access Hubs</h1>
      <p className="text-center text-gray-600 mb-10">Your community centers for resources, training, and support.</p>
      
     {currentUser && <div className="flex justify-center mb-8">
        <Link to='/add-new-hub'>
          <button
            type="button"
            className="inline-flex items-center gap-3 h-12 px-6 rounded-xl text-white font-semibold tracking-wide bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/30 hover:from-emerald-500 hover:to-emerald-700 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white transition-all"
          >
            <Plus className="h-5 w-5" />
            <span>Add New Hub</span>
          </button>
        </Link>
      </div>} 

      {loading && (
        <div className="text-center py-12">
          <div className="inline-flex items-center gap-2 text-gray-600">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
            <span>Loading hubs...</span>
          </div>
        </div>
      )}
      
      {error && (
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg border border-red-200">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}
      
      {!loading && !error && allHubs.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex flex-col items-center gap-3 text-gray-500">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <MapPin className="w-8 h-8 text-gray-400" />
            </div>
            <div>
              <p className="text-lg font-medium">No hubs yet</p>
              <p className="text-sm">Be the first to add one and help your community!</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {allHubs.map(hub => {
          const isMine = currentUser && hub.createdById === currentUser.uid;
          
          return (
            <div key={hub.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              {/* Header with delete button */}
              <div className="relative bg-gradient-to-r from-emerald-50 to-green-50 p-4 border-b border-gray-100">
              <div className="flex items-start justify-between">
              <h2 className="text-xl font-bold text-gray-800 pr-4">{hub.hubName}</h2>

              {isMine && (
                <button 
                  onClick={() => handleDelete(hub.id)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer border border-red-500 bg-red-50"
                  title="Delete hub"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {!isMine && currentUser && (
                (() => {
                  const alreadyJoined = (hub.members || []).some(
                    (m) => m.uid === currentUser.uid
                  );

                  return alreadyJoined ? (
                    <button
                      disabled
                      className="flex items-center gap-2 flex-shrink-0 px-4 py-2 border border-gray-300 
                                text-green-500 font-semibold rounded-lg cursor-not-allowed
                                bg-gray-100 transition-all duration-300"
                    >
                      <span>Joined</span>
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleJoinHub(hub.id)}
                      className="flex items-center gap-2 flex-shrink-0 px-4 py-2 border border-green-500 
                                text-green-600 font-semibold rounded-lg cursor-pointer
                                bg-green-50 hover:bg-green-500 hover:text-white 
                                transition-all duration-300 ease-in-out shadow-sm hover:shadow-md
                                transform hover:scale-105 active:scale-95"
                      title="Join hub"
                    >
                      <span>Join Hub</span>
                      <Plus className="w-4 h-4" />
                    </button>
                  );
                })()
              )}
            </div>

              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                {/* Location */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                    <MapPin className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Location</p>
                    <p className="text-gray-600">{hub.hubPlace}</p>
                  </div>
                </div>

                {/* Time */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                    <Clock className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Operating Hours</p>
                    <p className="text-gray-600">From: {hub.hubOpenTime}</p>
                    <p className="text-gray-600">To: {hub.hubCloseTime}</p>
                  </div>
                </div>

                          {/* Date */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                        <CalendarDays className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">Available Days</p>

                        <div className="flex flex-wrap gap-2 mt-1">
                          {Array.isArray(hub.hubOpenDate)
                            ? hub.hubOpenDate.map((day, idx) => (
                                <span
                                  key={`${hub.id}-day-${idx}`}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                                >
                                  {day}
                                </span>
                              ))
                            : (
                              <span className="text-gray-600">
                                {hub.hubOpenDate || "Not specified"}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>



                {/* Services */}
                <div className="pt-2">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Services Offered</h3>
                  <div className="flex flex-wrap gap-2">
                    {(hub.services || []).map((service, idx) => (
                      <span 
                        key={`${hub.id}-service-${idx}`}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

            {hub.members && hub.members.length > 0 && (
              <div className="pt-2">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Members ({hub.memberCount || hub.members.length})
                </h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  {hub.members.map((m, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <User className="w-4 h-4 text-emerald-500" />
                      {m.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

               
                {hub.createdBy && (

                  <div className="flex items-center gap-3 pt-2 border-t border-gray-100"
                  onClick={()=>{   
                    if(!currentUser) return navigate('/login');
                    if (currentUser?.uid === hub.createdById) {
                      navigate("/profile");
                    } else {
                      navigate(`/forign-profile/${hub.createdById}`);
                    }
                }} >
                    <div className="flex-shrink-0 w-5 h-5">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Created by</p>
                      <p className="text-sm font-medium text-gray-700 truncate"

                      >{hub.createdBy} {isMine&&'(me)'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


